import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller } from 'react-hook-form'
import { DateFormat } from '../../../helpers/formatting'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import LoxSelect from '../../../components/LoxSelect'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { setChangeMade } from '../../../redux/paymentSchedulePanelSlice'
import { setModal } from '../../../redux/modalSlice'
import ManagerApprovalIcons from '../../../components/elements/ManagerApprovalIcons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import PaymentDatePicker from "../../../components/PaymentDatePicker";
import { format } from 'date-fns';

export default function DeferPayment(props) {
  const { loanId, refreshPaymentSchedule, navigateSlider, currentDate, customerInfo, paymentMethodHelperText } = props
  const requesting = useRef(false)
  const request = useRequest()
  const dispatch = useDispatch()
  const [responseData, setResponseData] = useState([])
  const { register, handleSubmit, control, watch } = useForm();
  const [saving, setSaving] = useState(false)
  const dropdownOptions = useSelector(selectDropdownOptions);
  const [selectedDate, setSelectedDate] = useState(null);
  const [paymentMethodDdoId, setPaymentMethodDdoId] = useState(null);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const paymentMethodACHId = dropdownOptions.find((o) => o?.code_key === "payment_method_ach")?.id;

  useEffect(() => {
    let hasChange = false;
    if (paymentMethodDdoId !== defaultPaymentMethod?.value) {
      hasChange = true;
    }
    dispatch(setChangeMade(hasChange))
  }, [paymentMethodDdoId]);

  useEffect(() => {
    getNextDeferrablePayment(loanId)
  }, [])

  const getNextDeferrablePayment = async (loanId) => {
    if (requesting.current) return;
    requesting.current = true;
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/next_deferrable_payment`;
      // Check if the currentDate variable has a value and, if so, add it as a query parameter
      if (currentDate !== null) {
        endpoint += `?current_date=${currentDate}`;
      }
      request.setEndpoint(endpoint)
      const response = await request.get()
      setResponseData(response?.data?.data);
      if (!defaultPaymentMethod) {
        const paymentMethod = dropdownOptions.filter((o) => response?.data?.data?.default_payment_method_dropdown_option_id === o.id).map((o) => ({ label: o.name, value: o.id }))[0];
        setDefaultPaymentMethod(paymentMethod);
        setPaymentMethodDdoId(paymentMethod?.value)
        setSelectedDate(new Date((`${response?.data?.data?.max_defer_date}T00:00:00`)))
      }
      requesting.current = false;
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false;
    }
  }

  const onSubmit = async (formData) => {
    if (paymentMethodACHId == paymentMethodDdoId && customerInfo?.ach_revocation_active) {
        dispatch(
          setModal({
            is_active: true,
            type: 'allow_ach_payments_modal',
            options: { customerId: customerInfo.id, hasBkConfirmed: customerInfo?.has_bk_confirmed }
          })
        )
    } else {
      setSaving(true)
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/defer_payment`)
      try {
        if (currentDate !== null) {
          formData['current_date'] = currentDate;
        }
        formData.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id.value;
        formData.payment_date = format(selectedDate, 'yyyy-MM-dd');
        const response = await request.post(formData)
        if (response.status === 200 || response.status === 201) {
          setSaving(false)
          dispatch(setNotification({ type: 'success', body: '' }))
          navigateSlider("form_submit")
          refreshPaymentSchedule()
        } else {
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  };

  const checkChangesMade = () => {
    navigateSlider("");
  }

  return (
    <div className="pl-5 py-3">
      {responseData.length == 0 && <div className="is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }}>--- Loading ---</div>}

      {responseData.length != 0 && (
        <form onSubmit={handleSubmit(onSubmit)} id="defer_payment_form" data-testid="defer-payment-form">
          <p className="title is-5">Defer Payment {responseData?.payment_id} {responseData?.is_require_manager_approval && (<ManagerApprovalIcons type="mgr_approval_status_required" />)}</p>
          <p className="subtitle is-6">Only the next regular payment can be deferred</p>
          <table className="table is-narrow table-payment-schedule-panel">
            <thead>
              <tr>
                <th>
                  Payment Method <span
                    className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                    data-tooltip={paymentMethodHelperText}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ zIndex: 15, position: 'relative' }}>
                  <Controller
                    name="payment_method_dropdown_option_id"
                    defaultValue={defaultPaymentMethod}
                    control={control}
                    render={({ field }) => (
                      <LoxSelect
                        {...field}
                        {...register('payment_method_dropdown_option_id')}
                        testId="defer-payment-method"
                        options={dropdownOptions.filter((o) => responseData?.payment_method_dropdown_option_ids.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))}
                        isSearchable={false}
                        classNames={reactSelectClassNames()}
                        onChange={(val) => {
                          setPaymentMethodDdoId(val?.value)
                          field.onChange({ label: val?.label, value: val?.value })
                        }}
                        menuPosition="fixed"
                        form="defer_payment_form"
                        styles={{ menu: ({ ...css }) => ({ ...css, width: 'max-content' }) }}
                      />
                    )}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <table className="table is-narrow table-payment-schedule-panel">
            <thead>
              <tr>
                <th>ID #</th>
                <th>Defer Until Date</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    name="payment_id"
                    type="hidden"
                    value={responseData?.payment_id}
                    form="defer_payment_form"
                    {...register('payment_id')}
                  />
                  {responseData?.payment_id}
                </td>
                <td data-testid="defer-payment-date">
                  <Controller
                    name="payment_date"
                    control={control}
                    defaultValue={responseData?.max_defer_date}
                    render={({ field: { onChange } }) => (
                      <PaymentDatePicker
                        paymentMethodDdoId={paymentMethodDdoId}
                        max={`${responseData?.max_defer_date}T00:00:00`}
                        min={`${responseData?.min_defer_date}T00:00:00`}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        defaultToMax
                      />
                    )}
                  />
                </td>
                <td>Can&apos;t go past <DateFormat dateString={responseData?.max_defer_date} /></td>
                <td>
                  <div className="has-text-right">
                    <button
                      type="button"
                      data-testid="defer-payment-cancel"
                      className="button has-background-grey has-text-white is-small mr-1"
                      onClick={() => checkChangesMade()}
                    >
                      <span className="mx-3">Cancel</span>
                    </button>
                    <button
                      type="submit"
                      data-testid="defer-payment-submit"
                      className="button is-link is-small mr-1"
                      disabled={saving && 'disabled'}
                    >
                      <span className="mx-3">{saving ? 'Saving' : 'Save'}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </form>
      )}
    </div>
  )
}
